import {FunctionComponent, useEffect, useMemo, useState} from "react";
import { useSearchParams } from "react-router-dom";
import { Button, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight, DarkMode, Info, LightMode, RocketLaunch } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowTrendUp, faCrown, faGauge, faHandshake, faPlug } from "@fortawesome/free-solid-svg-icons";
import { faClock } from "@fortawesome/free-regular-svg-icons";

import {
    convertGetUserDataListToDisciplineDataList,
    CZK_UNIT,
    DISCIPLINE_LOCAL_STORAGE_KEY,
    EM_SPACE,
    EN_SPACE,
    formatMinutes,
    formatNumber,
    getAverageClimbPercentageFormatted,
    getAverageSpeedKmHFormatted,
    getAverageSpeedMin100mFormatted,
    getAverageSpeedMinKmFormatted,
    getDefaultDisciplineTitle,
    getDisciplineKmSum,
    getDisciplineMinSum,
    getDisciplineScoreSum,
    getScoreInUnits,
    getScoreWithUnitsForTitle,
    getTotalScore,
    hasClimberBadge,
    hasSpeedBadgeKmH,
    HOUR_UNIT,
    KM_UNIT,
    METER_UNIT,
    MIN_UNIT,
    MINUTES_IN_HOUR,
    parseYear,
    PRIMARY_COLOR,
    PRIMARY_FONT_SIZE,
    SCORE_THREASHOLD,
    UNBREAKING_SPACE,
    YEAR_SEARCH_PARAM,
} from "../utils";
import {DisciplineTitle, LocalResultsData, Title, UserResultData} from "../types";
import {prefetchResults, useResults} from "../hooks/useResults";
import {Spacer} from "./Spacer";
import {ContactFooter} from "./ContactFooter";
import {ColoredTitle} from "./ColoredTitle";
import {StravaLogo} from "./StravaLogo";
import {Loading} from "./Loading";
import {Error} from "./Error";
import {IconButtonHitArea} from "./IconButtonHitArea";
import {UpdateDialog} from "./UpdateDialog";
import {UpdateFooter} from "./UpdateFooter";
import {InfoDialog} from "./InfoDialog";
import {NewsDialog} from "./NewsDialog";
import {LoginDialog} from "./LoginDialog";
import {YearSelect} from "./YearSelect";

const GUEST_DESCRIPTION = "Host – bez pořadí, km se počítají do daru";

/** Older years lack some metrics (e.g. time and elevation gain before March 2023). */
const EMPTY_METRICS = {
    runKm: 0,
    runMin: 0,
    runElevationGain: 0,
    walkKm: 0,
    walkMin: 0,
    walkElevationGain: 0,
    rideKm: 0,
    rideMin: 0,
    rideElevationGain: 0,
    inlineKm: 0,
    inlineMin: 0,
    inlineElevationGain: 0,
    swimKm: 0,
    swimMin: 0,
    fitnessMin: 0,
};

const sumData = (data: LocalResultsData | null): UserResultData[] => Object.values(data?.data ?? {})
    .map((monthDataList) => Object.values(monthDataList)
        .filter((monthData) => monthData !== null)
        .map((monthData) => ({ ...EMPTY_METRICS, ...monthData })))
    .filter((valuableMonthDataList) => valuableMonthDataList.length > 0)
    .map((valuableMonthDataList) => valuableMonthDataList.slice(1).reduce((acc, value) => ({
        ...acc,
        eBike: acc.eBike ? acc.eBike : value.eBike,
        runKm: acc.runKm + value.runKm,
        runMin: acc.runMin + value.runMin,
        runElevationGain: acc.runElevationGain + value.runElevationGain,
        walkKm: acc.walkKm + value.walkKm,
        walkMin: acc.walkMin + value.walkMin,
        walkElevationGain: acc.walkElevationGain + value.walkElevationGain,
        rideKm: acc.rideKm + value.rideKm,
        rideMin: acc.rideMin + value.rideMin,
        rideElevationGain: acc.rideElevationGain + value.rideElevationGain,
        inlineKm: acc.inlineKm + value.inlineKm,
        inlineMin: acc.inlineMin + value.inlineMin,
        inlineElevationGain: acc.inlineElevationGain + value.inlineElevationGain,
        swimKm: acc.swimKm + value.swimKm,
        swimMin: acc.swimMin + value.swimMin,
        fitnessMin: acc.fitnessMin + value.fitnessMin,
    }), valuableMonthDataList[0]));

export type ContentProps = {
    isDark: boolean;
    onToggleDarkMode: () => void;
};

export const Content: FunctionComponent<ContentProps> = ({ isDark, onToggleDarkMode }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoginDialogOpened, setIsLoginDialogOpened] = useState(false);
    const [isInfoDialogOpened, setIsInfoDialogOpened] = useState(false);
    const [isNewsDialogOpened, setIsNewsDialogOpened] = useState(false);
    const [isUpdateDialogOpened, setIsUpdateDialogOpened] = useState(false);
    const [selectedDisciplineTitle, setSelectedDisciplineTitle] = useState<Title>(getDefaultDisciplineTitle);
    const requestedYear = parseYear(searchParams.get(YEAR_SEARCH_PARAM));
    const { response, isLoading, isError } = useResults(requestedYear);

    const disciplineDataList = useMemo(
        () => response === undefined ? [] : convertGetUserDataListToDisciplineDataList(sumData(response.data)),
        [response],
    );
    const selectedYear = response?.year;
    const latestYear = response?.years[0] ?? selectedYear;
    const isHistoric = selectedYear !== undefined && latestYear !== undefined && selectedYear < latestYear;
    const updatedTimestamp = response?.data?.timestamp;
    const failedCount = response?.data?.failedCount;
    const guests = response?.data?.guests ?? {};
    const hasGuests = Object.keys(guests).length > 0;
    const isGuest = (id: number) => guests[`${id}`] === true;

    const selectDisciplineTitle = (disciplineTitle: Title) => {
        setSelectedDisciplineTitle(disciplineTitle);
        localStorage.setItem(DISCIPLINE_LOCAL_STORAGE_KEY, disciplineTitle);
    }

    // The latest year has no parameter, so it stays the default and a shared link to it never goes stale.
    const selectYear = (year: number) => setSearchParams(year === latestYear ? {} : { [YEAR_SEARCH_PARAM]: `${year}` });

    useEffect(() => {
        if (disciplineDataList.length === 0) {
            document.title = "MoroKilometry";
            return;
        }
        const yearSuffix = isHistoric ? `${UNBREAKING_SPACE}${selectedYear}` : "";
        document.title = `MoroKilometry${yearSuffix}${EN_SPACE}–${EN_SPACE}${formatNumber(getTotalScore(disciplineDataList))}${UNBREAKING_SPACE}${CZK_UNIT}`;
    }, [disciplineDataList, isHistoric, selectedYear]);

    if (isError) {
        return <Error/>;
    }

    if (response === undefined) {
        return <Loading/>;
    }

    const selectedDiscipline = disciplineDataList.find((disciplineData) => disciplineData.title === selectedDisciplineTitle);

    if (selectedDiscipline === undefined) {
        return <Error/>;
    }

    const getPreviousDisciplineTitle = (): Title => {
        const index = disciplineDataList.findIndex((discipline) => discipline.title === selectedDisciplineTitle);

        if (index === 0) {
            return disciplineDataList[disciplineDataList.length - 1].title;
        } else {
            return disciplineDataList[index - 1].title;
        }
    }

    const selectPreviousDiscipline = () => selectDisciplineTitle(getPreviousDisciplineTitle());

    const getNextDisciplineTitle = (): Title => {
        const index = disciplineDataList.findIndex((discipline) => discipline.title === selectedDisciplineTitle);

        if (index === disciplineDataList.length - 1) {
            return disciplineDataList[0].title;
        } else {
            return disciplineDataList[index + 1].title;
        }
    }

    const selectNextDiscipline = () => selectDisciplineTitle(getNextDisciplineTitle());

    const visibleLeaderboard = selectedDiscipline.leaderboard.filter((member) => member.score > SCORE_THREASHOLD);
    // Guests have no rank, so the members below them keep their position.
    let rank = 0;
    const ranks = visibleLeaderboard.map((member) => isGuest(member.id) ? undefined : ++rank);

    return (
        <div className="content">
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                margin: 16
            }}>
                {isMobile ? (
                    <IconButton onClick={() => setIsInfoDialogOpened(true)} size="small">
                        <Info />
                    </IconButton>
                ) : (
                    <Button variant="outlined" disableElevation onClick={() => setIsInfoDialogOpened(true)}>
                        O co jde?
                    </Button>
                )}
                <div style={{marginLeft: isMobile ? 4 : 16}}>
                    {isMobile ? (
                        <IconButton onClick={() => setIsNewsDialogOpened(true)} size="small">
                            <RocketLaunch />
                        </IconButton>
                    ) : (
                        <Button variant="outlined" disableElevation onClick={() => setIsNewsDialogOpened(true)}>
                            Novinky
                        </Button>
                    )}
                </div>
                <div style={{marginLeft: isMobile ? 4 : 8}}>
                    <Tooltip title={isDark ? "Světlý režim" : "Tmavý režim"} followCursor={true}>
                        <IconButton onClick={onToggleDarkMode} size="small">
                            {isDark ? <LightMode /> : <DarkMode />}
                        </IconButton>
                    </Tooltip>
                </div>
                <div style={{display: "flex", justifyContent: "end", flex: 1}}>
                    <Tooltip title={"Přidej se k nám!"} followCursor={true}>
                        <img
                            className="strava-connect"
                            src="/btn_strava_connectwith_orange.png"
                            alt="Connect with Strava"
                            onClick={() => setIsLoginDialogOpened(true)}
                        />
                    </Tooltip>
                </div>
            </div>
            <ColoredTitle first="Moro" second="Kilometry"/>
            {response.years.length > 0 ? (
                <YearSelect
                    // The requested year right away, the loaded data follow.
                    year={requestedYear ?? latestYear ?? response.year}
                    years={response.years}
                    isLoading={isLoading}
                    onChange={selectYear}
                    onOpen={() => prefetchResults(response.years)}
                />
            ) : null}
            <div style={{opacity: isLoading ? 0.15 : 0.3, transition: "opacity 200ms"}}>
                {isHistoric ? (
                    <>Hodnota daru v roce {response.year} byla {formatNumber(getTotalScore(disciplineDataList))}{UNBREAKING_SPACE}{CZK_UNIT}.</>
                ) : (
                    <>Aktuální hodnota daru je {formatNumber(getTotalScore(disciplineDataList))}{UNBREAKING_SPACE}{CZK_UNIT}.</>
                )}
            </div>
            {isHistoric && latestYear !== undefined ? (
                <div className="historic-banner">
                    <span>Prohlížíš ročník {response.year}</span>
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={() => selectYear(latestYear)}
                        sx={{ borderRadius: 999, textTransform: "none", color: "rgb(37, 39, 70)" }}
                    >
                        Zpět na {latestYear} →
                    </Button>
                </div>
            ) : null}
            <Spacer/>
            <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center"}}>
                <h1 style={{
                    textDecoration: "underline",
                    textDecorationColor: PRIMARY_COLOR,
                    opacity: 0.05
                }}>{getPreviousDisciplineTitle()[getPreviousDisciplineTitle().length - 2]}</h1>
                <h1 style={{
                    textDecoration: "underline",
                    textDecorationColor: PRIMARY_COLOR,
                    opacity: 0.1
                }}>{getPreviousDisciplineTitle()[getPreviousDisciplineTitle().length - 1]}</h1>
                <IconButton color="primary" onClick={selectPreviousDiscipline}>
                    <IconButtonHitArea/>
                    <ChevronLeft/>
                </IconButton>
                <h1 style={{
                    textDecoration: "underline",
                    textDecorationColor: PRIMARY_COLOR
                }}>{selectedDiscipline.title}</h1>
                <IconButton color="primary" onClick={selectNextDiscipline}>
                    <IconButtonHitArea/>
                    <ChevronRight/>
                </IconButton>
                <h1 style={{
                    textDecoration: "underline",
                    textDecorationColor: PRIMARY_COLOR,
                    opacity: 0.1
                }}>{getNextDisciplineTitle()[0]}</h1>
                <h1 style={{
                    textDecoration: "underline",
                    textDecorationColor: PRIMARY_COLOR,
                    opacity: 0.05
                }}>{getNextDisciplineTitle()[1]}</h1>
            </div>
            <Spacer/>
            <div className="wrapper" aria-busy={isLoading} style={{opacity: isLoading ? 0.3 : 1, transition: "opacity 200ms"}}>
                <table className="table">
                    <tbody>
                    <tr className="header-line">
                        <td className="collapsed" style={{textAlign: "end"}}>#</td>
                        <td style={{textAlign: "start"}}>Jméno</td>
                        <Tooltip title={selectedDiscipline.unit === MIN_UNIT ? "10 min = 1 Kč" : "1 km = 1 Kč"}
                                 followCursor={true}>
                            <td className="collapsed" style={{textAlign: "end"}}>Skóre</td>
                        </Tooltip>
                    </tr>
                    {visibleLeaderboard.map((member, index) => {
                        const nameTooltip = <>
                            {disciplineDataList.map((disciplineData) => {
                                const leaderboardData = disciplineData.leaderboard.find((leaderboardData) => leaderboardData.id === member.id);
                                if (leaderboardData !== undefined) {
                                    return (
                                        <>
                                            <Typography
                                                variant="caption"
                                                color={leaderboardData.score < SCORE_THREASHOLD ? "rgba(255, 255, 255, 0.5)" : "white"}
                                                key={disciplineData.title}
                                                style={{display: "flex", flexDirection: "row"}}
                                            >
                                                {disciplineData.title}{EM_SPACE}<span
                                                style={{flex: 1}}/>{getScoreWithUnitsForTitle(disciplineData.unit, leaderboardData.km, leaderboardData.min)}
                                            </Typography>
                                        </>
                                    )
                                } else {
                                    return null
                                }
                            })}
                        </>;

                        return (
                            <tr key={member.id} className="line">
                                <td className="collapsed" style={{textAlign: "end", opacity: "0.3"}}>
                                    <div>
                                        <span>
                                        {ranks[index]}
                                    </span>
                                        <br/>
                                        <span style={{color: PRIMARY_COLOR}}>
                                        {UNBREAKING_SPACE}
                                    </span>
                                    </div>
                                </td>
                                <td style={{textAlign: "start"}}>
                                    <div className="table-left-container">
                                        <a
                                            target="_blank"
                                            rel="noreferrer"
                                            href={`https://www.strava.com/athletes/${member.id}`}
                                        >
                                            <Tooltip title={nameTooltip} followCursor={true}>
                                                <div className="table-name-container">
                                                    <span style={{fontSize: PRIMARY_FONT_SIZE}}>
                                                        {member.firstname}
                                                    </span>
                                                    <span style={{color: PRIMARY_COLOR}}>
                                                        {member.lastname}
                                                    </span>
                                                </div>
                                            </Tooltip>
                                        </a>
                                        <div className="table-badge-container">
                                            {(selectedDiscipline.title === DisciplineTitle.Ride &&
                                                hasClimberBadge(member, 1000, 110)) ? (
                                                <Tooltip
                                                    title={`Vrchař – Ø ${getAverageClimbPercentageFormatted(member)} %`}
                                                    // title={`Vrchař – Ø ${getAverageClimbPercentageFormatted(member)} % (> 1 000 km a Ø > 1,1 %)`}
                                                    followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faCrown}/>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                            {(selectedDiscipline.title === DisciplineTitle.Run &&
                                                hasClimberBadge(member, 100, 120)) ? (
                                                <Tooltip
                                                    title={`Vrchař – Ø ${getAverageClimbPercentageFormatted(member)} %`}
                                                    // title={`Vrchař – Ø ${getAverageClimbPercentageFormatted(member)} % (> 100 km a Ø > 1,2 %)`}
                                                    followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faCrown}/>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                            {(selectedDiscipline.title === DisciplineTitle.Walk &&
                                                hasClimberBadge(member, 100, 200)) ? (
                                                <Tooltip
                                                    title={`Vrchař – Ø ${getAverageClimbPercentageFormatted(member)} %`}
                                                    // title={`Vrchař – Ø ${getAverageClimbPercentageFormatted(member)} % (> 100 km a Ø > 2 %)`}
                                                    followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faCrown}/>
                                                    </span>
                                                </Tooltip>
                                            ) : null}

                                            {(selectedDiscipline.title === DisciplineTitle.Ride &&
                                                hasSpeedBadgeKmH(member, 1000, 23)) ? (
                                                <Tooltip
                                                    title={`Sprinter – Ø ${getAverageSpeedKmHFormatted(member)} km/h`}
                                                    // title={`Sprinter – Ø ${getAverageSpeedKmHFormatted(member)} km/h (> 1 000 km a Ø > 23 km/h)`}
                                                    followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faGauge}/>
                                                        {/*<FontAwesomeIcon icon={solid('wind')}/>*/}
                                                        {/*<FontAwesomeIcon icon={solid('gauge-simple-high')}/>*/}
                                                        {/*<FontAwesomeIcon icon={solid('gauge-high')}/>*/}
                                                        {/*<FontAwesomeIcon icon={solid('forward')}/>*/}
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                            {(selectedDiscipline.title === DisciplineTitle.Run &&
                                                hasSpeedBadgeKmH(member, 100, 10.9091)) ? ( // 5:30/km = 10.9091 km/h
                                                <Tooltip
                                                    title={`Sprinter – Ø ${getAverageSpeedMinKmFormatted(member)}`}
                                                    // title={`Sprinter – Ø ${getAverageSpeedMinKmFormatted(member)}(> 100 km a Ø < 5:30/km)`}
                                                    followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faGauge}/>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                            {(selectedDiscipline.title === DisciplineTitle.Walk &&
                                                hasSpeedBadgeKmH(member, 100, 5)) ? (
                                                <Tooltip
                                                    title={`Sprinter – Ø ${getAverageSpeedKmHFormatted(member)} km/h`}
                                                    // title={`Sprinter – Ø ${getAverageSpeedKmHFormatted(member)} km/h (> 100 km a Ø > 5 km/h)`}
                                                    followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faGauge}/>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                            {(selectedDiscipline.title === DisciplineTitle.Swim &&
                                                hasSpeedBadgeKmH(member, 10, 2.4)) ? ( // 2:30 min/100m = 2.4 km/h
                                                <Tooltip
                                                    title={`Sprinter – Ø ${getAverageSpeedMin100mFormatted(member)}`}
                                                    // title={`Sprinter – Ø ${getAverageSpeedMin100mFormatted(member)} (> 10 km a Ø < 2:30/100 m)`}
                                                    followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faGauge}/>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                            {(selectedDiscipline.title === DisciplineTitle.Inline &&
                                                hasSpeedBadgeKmH(member, 100, 17)) ? (
                                                <Tooltip
                                                    title={`Sprinter – Ø ${getAverageSpeedKmHFormatted(member)} km/h`}
                                                    // title={`Sprinter – Ø ${getAverageSpeedKmHFormatted(member)} km/h (> 100 km a Ø > 18 km/h)`}
                                                    followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faGauge}/>
                                                    </span>
                                                </Tooltip>
                                            ) : null}

                                            {member.eBike ? (
                                                <Tooltip title={"E-Biker (alespoň jedna aktivita na E-Biku)"}
                                                         followCursor={true}>
                                                        <span className="badge">
                                                            <FontAwesomeIcon icon={faPlug}/>
                                                        </span>
                                                </Tooltip>
                                            ) : null}
                                            {isGuest(member.id) ? (
                                                <Tooltip title={GUEST_DESCRIPTION} followCursor={true}>
                                                    <span className="badge">
                                                        <FontAwesomeIcon icon={faHandshake}/>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                        </div>
                                    </div>
                                </td>
                                <td className="collapsed" style={{textAlign: "end"}}>
                                    <div className="grid-container">
                                        <Tooltip title={
                                            `${getScoreWithUnitsForTitle(selectedDiscipline.unit, member.km, member.min)} ~ ${Math.floor(member.score)} ${CZK_UNIT}`
                                        } followCursor={true}>
                                            <div className="grid-item score">
                                            <span style={{fontSize: PRIMARY_FONT_SIZE}}>
                                                {member.score < SCORE_THREASHOLD
                                                    ? (<span style={{opacity: 0.3}}>–</span>)
                                                    : getScoreInUnits(selectedDiscipline.unit, member.km, member.min)}
                                            </span>
                                                <span style={{color: PRIMARY_COLOR}}>
                                                {UNBREAKING_SPACE}
                                                    {selectedDiscipline.unit === MIN_UNIT ? HOUR_UNIT : KM_UNIT}
                                            </span>
                                            </div>
                                        </Tooltip>

                                        <div className="grid-item time-and-climb">
                                            {selectedDiscipline.title !== DisciplineTitle.Fitness &&
                                                <Tooltip title={`Celkový čas: ${formatMinutes(member.min)}`}
                                                         followCursor={true}>
                                                <span className="time">
                                                    <FontAwesomeIcon icon={faClock}/>
                                                    {UNBREAKING_SPACE}
                                                    {member.min < SCORE_THREASHOLD
                                                        ? (<span style={{opacity: 0.3}}>–</span>)
                                                        : (formatNumber(Math.floor(member.min / MINUTES_IN_HOUR)))}
                                                    {UNBREAKING_SPACE}
                                                    {HOUR_UNIT}
                                                </span>
                                                </Tooltip>
                                            }

                                            {selectedDiscipline.title !== DisciplineTitle.Fitness && selectedDiscipline.title !== DisciplineTitle.Swim &&
                                                <Tooltip
                                                    title={`Nastoupáno: ${formatNumber(member.elevationGain)} ${METER_UNIT}`}
                                                    followCursor={true}>
                                                <span className="climb">
                                                    <FontAwesomeIcon icon={faArrowTrendUp}/>
                                                    {UNBREAKING_SPACE}
                                                    {member.elevationGain < SCORE_THREASHOLD ? (
                                                        <span style={{opacity: 0.3}}>–</span>
                                                    ) : (
                                                        formatNumber(Math.floor(member.elevationGain / 1000))
                                                    )}
                                                    {UNBREAKING_SPACE}
                                                    {KM_UNIT}
                                                </span>
                                                </Tooltip>
                                            }
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    <tr className="sum">
                        <td/>
                        <td style={{textAlign: "left"}}>
                            <span style={{fontSize: PRIMARY_FONT_SIZE}}>Celkem v kategorii</span>
                        </td>
                        <Tooltip title={
                            `${getScoreWithUnitsForTitle(selectedDiscipline.unit, getDisciplineKmSum(selectedDiscipline), getDisciplineMinSum(selectedDiscipline))}`
                        } followCursor={true}>
                            <td style={{textAlign: "right"}}>
                                <span style={{fontSize: PRIMARY_FONT_SIZE}}>
                                    {formatNumber(getDisciplineScoreSum(selectedDiscipline))}
                                </span>
                                <span style={{color: PRIMARY_COLOR}}>
                                    {UNBREAKING_SPACE}
                                    {CZK_UNIT}
                                </span>
                            </td>
                        </Tooltip>
                    </tr>
                    <tr className="sum">
                        <td/>
                        <td style={{textAlign: "left"}} colSpan={2}>
                            {selectedDiscipline.title === DisciplineTitle.Ride ? (
                                <>
                                    <span className="badge"><FontAwesomeIcon icon={faCrown}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}najeto ≥ 1 000 km a Ø převýšení ≥ 1,1 %`}</span><br/>
                                    <span className="badge"><FontAwesomeIcon icon={faGauge}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}najeto ≥ 1 000 km a Ø rychlost ≥ 23 km/h`}</span><br/>
                                    <span className="badge"><FontAwesomeIcon icon={faPlug}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}alespoň jedna aktivita na E-Biku`}</span><br/>
                                </>
                            ) : null}
                            {selectedDiscipline.title === DisciplineTitle.Run ? (
                                <>
                                    <span className="badge"><FontAwesomeIcon icon={faCrown}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}naběháno ≥ 100 km a Ø převýšení ≥ 1,2 %`}</span><br/>
                                    <span className="badge"><FontAwesomeIcon icon={faGauge}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}naběháno ≥ 100 km a Ø tempo ≤ 5:30/km`}</span><br/>
                                </>
                            ) : null}
                            {selectedDiscipline.title === DisciplineTitle.Walk ? (
                                <>
                                    <span className="badge"><FontAwesomeIcon icon={faCrown}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}nachozeno ≥ 100 km a Ø převýšení ≥ 2 %`}</span><br/>
                                    <span className="badge"><FontAwesomeIcon icon={faGauge}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}nachozeno ≥ 100 km a Ø rychlost ≥ 5 km/h`}</span><br/>
                                </>
                            ) : null}
                            {selectedDiscipline.title === DisciplineTitle.Swim ? (
                                <>
                                    <span className="badge"><FontAwesomeIcon icon={faGauge}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}naplaváno ≥ 10 km a Ø tempo ≤ 2:30/100 m`}</span><br/>
                                </>
                            ) : null}
                            {selectedDiscipline.title === DisciplineTitle.Inline ? (
                                <>
                                    <span className="badge"><FontAwesomeIcon icon={faGauge}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}najeto ≥ 100 km a Ø rychlost ≥ 17 km/h`}</span><br/>
                                </>
                            ) : null}
                            {hasGuests ? (
                                <>
                                    <span className="badge"><FontAwesomeIcon icon={faHandshake}/></span>
                                    <span className="badgeInfo">{`${EN_SPACE}${GUEST_DESCRIPTION.toLowerCase()}`}</span><br/>
                                </>
                            ) : null}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <div>

            </div>
            <Spacer/>
            {
                updatedTimestamp ? (
                    <UpdateFooter
                        timestamp={updatedTimestamp}
                        failedCount={failedCount}
                        finalYear={isHistoric ? response.year : undefined}
                        onOpenDescription={() => setIsUpdateDialogOpened(true)}
                    />
                ) : null
            }
            <Spacer/>
            <StravaLogo/>
            <ContactFooter/>
            <Spacer/>
            <Spacer/>
            <LoginDialog isOpened={isLoginDialogOpened} onClose={() => setIsLoginDialogOpened(false)}/>
            <UpdateDialog isOpened={isUpdateDialogOpened} onClose={() => setIsUpdateDialogOpened(false)}/>
            <InfoDialog isOpened={isInfoDialogOpened} onClose={() => setIsInfoDialogOpened(false)}/>
            <NewsDialog isOpened={isNewsDialogOpened} onClose={() => setIsNewsDialogOpened(false)}/>
        </div>
    )
        ;
};
